import { formatDate, formatDateTime } from "features/discover/functions";
import { BooleanChoices } from "features/discover/types";
import { getAvailabilityOnlineOfflineChoices } from "../../availabilityFilterValue";
import { renderAvgDays, renderBoolean, renderNumber, renderOnlineOfflineAvailability, renderPercent } from "../../cellRenderers";
import type { ColumnConfig, FilterConfig } from "../../types";
import { OPS_MGMT_CLUB_SUMMARY_FILTER_EMPTY } from "./filterConstants";
/**
 * Type for a single row in the Clubs tab.
 * Data per Club.
 */
export type ClubsRow = Record<string, unknown>;

/**
 * Column definitions for Clubs tab.
 * Shows columns where "Country = Yes" or "Club = Yes" in the spec.
 * Displays data per Club.
 */
export const clubsColumns: ColumnConfig<ClubsRow>[] = [
	// ========== Overview Group ==========
	{
		key: "continent",
		label: "Continent",
		sortable: true,
		sortField: "continent",
		cell: (value) => (value as string) || "-",
		group: "Overview",
		defaultVisible: true,
		requiredFields: ["continent"],
		width: 100,
	},
	{
		key: "country",
		label: "Country",
		sortable: true,
		sortField: "countryName",
		cell: (_value, row) => (row?.countryName as string) || (row?.country as string) || "-",
		group: "Overview",
		defaultVisible: true,
		requiredFields: ["country", "countryName"],
		width: 130,
	},
	{
		key: "club",
		label: "Club",
		sortable: true,
		sortField: "club",
		cell: (value) => (value as string) || "-",
		group: "Overview",
		defaultVisible: true,
		requiredFields: ["club"],
		width: 150,
	},
	{
		key: "manager",
		label: "Manager",
		sortable: true,
		sortField: "manager",
		cell: (value) => (value as string) || "-",
		group: "Overview",
		defaultVisible: true,
		requiredFields: ["manager"],
		width: 140,
	},

	// ========== Partner Club Stats Group ==========
	{
		key: "onboardedDate",
		label: "Onboarded Date",
		sortable: true,
		sortField: "onboardedDate",
		cell: (value) => formatDate(value as string | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["onboardedDate"],
		width: 140,
	},
	{
		key: "numberOfScoutTeams",
		label: "Number of Scout Teams",
		sortable: true,
		sortField: "numberOfScoutTeams",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["numberOfScoutTeams"],
		summable: true, // Sum of Rows
		width: 180,
	},
	{
		key: "numberOfDiscoverTeams",
		label: "Number of Discover Teams",
		sortable: true,
		sortField: "numberOfDiscoverTeams",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["numberOfDiscoverTeams"],
		summable: true, // Sum of Rows
		width: 190,
	},
	{
		key: "consistentRunningPlayerPercent",
		label: "Consistent Running Player %",
		sortable: true,
		sortField: "consistentRunningPlayerPercent",
		cell: (value) => renderPercent(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["consistentRunningPlayerPercent"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 200,
	},
	{
		key: "variablePlayerQualityPercent",
		label: "Variable Player Quality %",
		sortable: true,
		sortField: "variablePlayerQualityPercent",
		cell: (value) => renderPercent(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["variablePlayerQualityPercent"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 200,
	},
	{
		key: "ntPlayers",
		label: "NT Players",
		sortable: true,
		sortField: "ntPlayers",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["ntPlayers"],
		summable: true, // Sum of Rows
		width: 110,
	},
	{
		key: "nonEbCamera",
		label: "Non-EB Camera",
		sortable: true,
		sortField: "nonEbCamera",
		cell: (value) => renderBoolean(value as boolean | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["nonEbCamera"],
		width: 130,
	},
	{
		key: "teamsheetAvailability",
		label: "Teamsheet Availability",
		sortable: true,
		sortField: "teamsheetAvailability",
		cell: (value) => renderOnlineOfflineAvailability(value),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["teamsheetAvailability"],
		width: 180,
	},
	{
		key: "fixtureAvailability",
		label: "Fixture Availability",
		sortable: true,
		sortField: "fixtureAvailability",
		cell: (value) => renderOnlineOfflineAvailability(value),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["fixtureAvailability"],
		width: 170,
	},
	{
		key: "lastPartnersUsage",
		label: "Last Partners Usage",
		sortable: true,
		sortField: "lastPartnersUsage",
		cell: (value) => formatDateTime(value as string | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["lastPartnersUsage"],
		width: 180,
	},

	// ========== Game Volumes Group ==========
	{
		key: "totalGames",
		label: "Total Games",
		sortable: true,
		sortField: "totalGames",
		cell: (value) => renderNumber(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["totalGames"],
		summable: true, // Sum of Rows
		width: 130,
	},
	{
		key: "gamesTagged",
		label: "Games Tagged",
		sortable: true,
		sortField: "gamesTagged",
		cell: (value) => renderNumber(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["gamesTagged"],
		summable: true, // Sum of Rows
		width: 130,
	},
	{
		key: "percentOfGamesTagged",
		label: "Proportion of Games Tagged",
		sortable: true,
		sortField: "percentOfGamesTagged",
		cell: (value) => renderPercent(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["percentOfGamesTagged"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 150,
	},
	{
		key: "avgGamePriority",
		label: "Avg Game Priority",
		sortable: true,
		sortField: "avgGamePriority",
		cell: (value) => {
			if (value == null) return "-";
			return (value as number).toFixed(2);
		},
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["avgGamePriority"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 150,
	},
	{
		key: "avgGameAnalysisProcessing",
		label: "Avg Game Analysis Processing",
		sortable: true,
		sortField: "avgGameAnalysisProcessing",
		cell: (value) => renderAvgDays(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["avgGameAnalysisProcessing"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 220,
	},

	// ========== Market Value Group ==========
	{
		key: "viewsOfGames",
		label: "Views of Games",
		sortable: true,
		sortField: "viewsOfGames",
		cell: (value) => renderNumber(value as number | null),
		group: "Market Value",
		defaultVisible: true,
		requiredFields: ["viewsOfGames"],
		summable: true, // Sum of Rows
		width: 140,
	},
	{
		key: "proportionOfGameViews",
		label: "Proportion of Game Views",
		sortable: true,
		sortField: "proportionOfGameViews",
		cell: (value) => renderPercent(value as number | null),
		group: "Market Value",
		defaultVisible: true,
		requiredFields: ["proportionOfGameViews"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 190,
	},

	// ========== Legal Group ==========
	{
		key: "contract",
		label: "Contract",
		sortable: true,
		sortField: "contract",
		cell: (value) => renderBoolean(value as boolean | null),
		group: "Legal",
		defaultVisible: true,
		requiredFields: ["contract"],
		width: 100,
	},
	{
		key: "contractEndDate",
		label: "Contract End Date",
		sortable: true,
		sortField: "contractEndDate",
		cell: (value) => formatDate(value as string | null),
		group: "Legal",
		defaultVisible: true,
		requiredFields: ["contractEndDate"],
		width: 150,
	},
	{
		key: "consent",
		label: "Consent",
		sortable: true,
		sortField: "consent",
		cell: (value) => renderBoolean(value as boolean | null),
		group: "Legal",
		defaultVisible: true,
		requiredFields: ["consent"],
		width: 100,
	},
	{
		key: "marketingOptIn",
		label: "Marketing Opt-in",
		sortable: true,
		sortField: "marketingOptIn",
		cell: (value) => renderBoolean(value as boolean | null),
		group: "Legal",
		defaultVisible: true,
		requiredFields: ["marketingOptIn"],
		width: 140,
	},
];

/**
 * Base endpoint for filter options.
 */
const FILTER_OPTIONS_ENDPOINT = "/admin/operations-management/summary/by-club/filter-options";

/**
 * Filter definitions for Clubs tab.
 * Ordered to match column groups.
 * Factory so translated option labels are resolved after i18n is ready.
 */
export function getClubsFilters(): FilterConfig[] {
	return [
	// ========== Overview Group ==========
	{
		id: "continent",
		label: "Continent",
		type: "select",
		field: "continent",
		filterType: "exact",
		options: [], // Will be populated dynamically from API
		optionsEndpoint: {
			url: FILTER_OPTIONS_ENDPOINT,
			filterParam: "filter",
		},
		placeholder: "Select continent...",
	},
	{
		id: "country",
		label: "Country",
		type: "multiselect",
		field: "country",
		filterType: "inarray",
		options: [], // Will be populated dynamically from API
		optionsEndpoint: {
			url: FILTER_OPTIONS_ENDPOINT,
			filterParam: "filter",
		},
		placeholder: "Select countries...",
	},
	{
		id: "club",
		label: "Club",
		type: "multiselect",
		field: "club",
		filterType: "inarray",
		optionsEndpoint: {
			url: FILTER_OPTIONS_ENDPOINT,
			filterParam: "filter",
		},
	},
	{
		id: "manager",
		label: "Manager",
		type: "multiselect",
		field: "manager",
		filterType: "inarray",
		wideMenu: true,
		optionsEndpoint: {
			url: FILTER_OPTIONS_ENDPOINT,
			filterParam: "filter",
		},
	},

	// ========== Partner Club Stats Group ==========
	{
		id: "onboardedDate",
		label: "Onboarded Date",
		type: "daterange",
		field: "onboardedDate",
		filterType: "datetimerange",
	},
	{
		id: "numberOfScoutTeams",
		label: "Number of Scout Teams",
		type: "numberrange",
		field: "numberOfScoutTeams",
		filterType: "numberrange",
		placeholder: "Number of Scout Teams...",
	},
	{
		id: "numberOfDiscoverTeams",
		label: "Number of Discover Teams",
		type: "numberrange",
		field: "numberOfDiscoverTeams",
		filterType: "numberrange",
		placeholder: "Number of Discover Teams...",
	},
	{
		id: "consistentRunningPlayerPercent",
		label: "Consistent Running Player %",
		type: "numberrange",
		field: "consistentRunningPlayerPercent",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},
	{
		id: "variablePlayerQualityPercent",
		label: "Variable Player Quality %",
		type: "numberrange",
		field: "variablePlayerQualityPercent",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},
	{
		id: "ntPlayers",
		label: "NT Players",
		type: "numberrange",
		field: "ntPlayers",
		filterType: "numberrange",
		placeholder: "NT Players...",
	},
	{
		id: "nonEbCamera",
		label: "Non-EB Camera",
		type: "boolean",
		field: "nonEbCamera",
		filterType: "boolean",
		options: BooleanChoices,
	},
	{
		id: "teamsheetAvailability",
		label: "Teamsheet Availability",
		type: "select",
		field: "teamsheetAvailability",
		filterType: "exact",
		options: getAvailabilityOnlineOfflineChoices(),
	},
	{
		id: "fixtureAvailability",
		label: "Fixture Availability",
		type: "select",
		field: "fixtureAvailability",
		filterType: "exact",
		options: getAvailabilityOnlineOfflineChoices(),
	},
	{
		id: "lastPartnersUsage",
		label: "Last Partners Usage",
		type: "daterange",
		field: "lastPartnersUsage",
		filterType: "datetimerange",
	},

	// ========== Game Volumes Group ==========
	{
		id: "totalGames",
		label: "Total Games",
		type: "numberrange",
		field: "totalGames",
		filterType: "numberrange",
		placeholder: "Total Games...",
	},
	{
		id: "gamesTagged",
		label: "Games Tagged",
		type: "numberrange",
		field: "gamesTagged",
		filterType: "numberrange",
		placeholder: "Games Tagged...",
	},
	{
		id: "percentOfGamesTagged",
		label: "% of Games Tagged",
		type: "numberrange",
		field: "percentOfGamesTagged",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},
	{
		id: "avgGamePriority",
		label: "Avg Game Priority",
		type: "numberrange",
		field: "avgGamePriority",
		filterType: "numberrange",
		placeholder: "Avg Game Priority...",
	},
	{
		id: "avgGameAnalysisProcessing",
		label: "Avg Game Analysis Processing",
		type: "numberrange",
		field: "avgGameAnalysisProcessing",
		filterType: "numberrange",
		placeholder: "Days...",
	},

	// ========== Market Value Group ==========
	{
		id: "viewsOfGames",
		label: "Views of Games",
		type: "numberrange",
		field: "viewsOfGames",
		filterType: "numberrange",
		placeholder: "Views of Games...",
	},
	{
		id: "proportionOfGameViews",
		label: "Proportion of Game Views",
		type: "numberrange",
		field: "proportionOfGameViews",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},

	// ========== Legal Group ==========
	{
		id: "contract",
		label: "Has Contract",
		type: "boolean",
		field: "contract",
		filterType: "boolean",
		options: BooleanChoices,
	},
	{
		id: "contractEndDate",
		label: "Contract End Date",
		type: "daterange",
		field: "contractEndDate",
		filterType: "datetimerange",
	},
	{
		id: "consent",
		label: "Has Consent",
		type: "boolean",
		field: "consent",
		filterType: "boolean",
		options: BooleanChoices,
	},
	{
		id: "marketingOptIn",
		label: "Marketing Opt-In",
		type: "boolean",
		field: "marketingOptIn",
		filterType: "boolean",
		options: BooleanChoices,
	},
	];
}
